// StudentDashboard.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Container,
  Card,
  Row,
  Col,
  Table,
  Badge,
  Button,
  Spinner,
  Alert,
} from "react-bootstrap";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from "chart.js";
import { Bar, Pie } from "react-chartjs-2";
import {
  Folder,
  Clock,
  Hourglass,
  Trophy,
  Flame,
  ShieldAlert,
  Sun,
  Sunset,
  Moon,
  Bell,
  Medal,
} from "lucide-react";
import NotificationIcon from "../../components/Notification";
import { useUser } from "../../contextProviders/userContext";
import Sidebar from "../../components/SideBar";
import TopNavBar from "../../components/topNavBar";
import { API_BASE_URL } from "../../api";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// CSD Brand palette for charts
const COLORS = [
  "#1B3A87",
  "#2A9D8F",
  "#B5832A",
  "#C1392B",
  "#3DBDAE",
  "#2E52B2",
  "#8C93AE",
  "#4CAF82",
  "#E67E22",
];

// ── Rank badge helper ─────────────────────────────────────
const ordinal = (rank) => {
  const remainder100 = rank % 100;
  if (remainder100 >= 11 && remainder100 <= 13) return `${rank}th`;
  if (rank % 10 === 1) return `${rank}st`;
  if (rank % 10 === 2) return `${rank}nd`;
  if (rank % 10 === 3) return `${rank}rd`;
  return `${rank}th`;
};

const RankBadge = ({ rank }) => {
  const configs = [
    { bg: "rgba(181,131,42,0.12)", color: "#B5832A", label: "1st" },
    { bg: "rgba(140,147,174,0.15)", color: "#5A6282", label: "2nd" },
    { bg: "rgba(193,57,43,0.10)", color: "#C1392B", label: "3rd" },
  ];
  const cfg = configs[rank - 1] || {
    bg: "rgba(27,58,135,0.08)",
    color: "var(--color-primary)",
    label: ordinal(rank),
  };
  return (
    <span
      style={{
        background: cfg.bg,
        color: cfg.color,
        fontWeight: 700,
        fontSize: "0.75rem",
        padding: "0.2em 0.7em",
        borderRadius: "99px",
        display: "inline-flex",
        alignItems: "center",
        gap: "0.3rem",
      }}
    >
      {rank === 1 && <Medal size={12} />}
      {cfg.label}
    </span>
  );
};

const LeaderboardTable = ({
  data,
  loading,
  colLabel,
  showAll,
  onToggle,
  highlightCurrentUser = false,
}) => {
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-4">
        <Spinner animation="border" size="sm" style={{ color: "var(--color-primary)" }} />
      </div>
    );
  }

  if (!data?.length) {
    return <p style={{ color: "var(--color-text-muted)", fontSize: "0.875rem" }}>No hours logged yet.</p>;
  }

  const visibleRows = showAll ? data : data.slice(0, 10);
  const currentUser = highlightCurrentUser
    ? data.find((item) => item.isCurrentUser)
    : null;
  const showPinnedPosition =
    !showAll && currentUser && !visibleRows.some((item) => item.isCurrentUser);

  const renderRow = (item, pinned = false) => (
    <tr
      key={`${item.name}-${item.rank}${pinned ? "-pinned" : ""}`}
      style={
        item.isCurrentUser
          ? {
              backgroundColor: "rgba(42, 157, 143, 0.12)",
            }
          : undefined
      }
    >
      <td><RankBadge rank={item.rank} /></td>
      <td style={{ fontWeight: 600, color: "var(--color-text)" }}>
        {item.name}
        {item.isCurrentUser && (
          <Badge className="ms-2" style={{ backgroundColor: "var(--color-teal)" }}>
            You
          </Badge>
        )}
      </td>
      <td className="text-end">
        <span className="status-approved">{Number(item.hours || 0).toFixed(1)} hrs</span>
      </td>
    </tr>
  );

  return (
    <>
      <div className="table-responsive">
        <table className="wcco-table">
          <thead>
            <tr>
              <th style={{ width: "70px" }}>Rank</th>
              <th>{colLabel}</th>
              <th className="text-end">Hours</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((item) => renderRow(item))}
            {showPinnedPosition && (
              <>
                <tr>
                  <td colSpan="3" className="text-center py-2" style={{ color: "var(--color-text-muted)", fontSize: "0.78rem" }}>
                    Your position
                  </td>
                </tr>
                {renderRow(currentUser, true)}
              </>
            )}
          </tbody>
        </table>
      </div>
      {data.length > 10 && (
        <div className="text-center mt-3">
          <Button
            variant="link"
            size="sm"
            className="text-decoration-none"
            style={{ color: "var(--color-primary)", fontWeight: 600 }}
            onClick={onToggle}
          >
            {showAll ? "Show top 10" : `View all ${data.length}`}
          </Button>
        </div>
      )}
    </>
  );
};

const StudentDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [participationData, setParticipationData] = useState({});
  const [error, setError] = useState(null);
  const { user } = useUser();
  const [totalHours, setTotalHours] = useState(0);
  const [pendingHours, setPendingHours] = useState(0);
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState({ topVolunteers: [], topProjects: [] });
  const [leaderboardLoading, setLeaderboardLoading] = useState(true);
  const [expandedLeaderboards, setExpandedLeaderboards] = useState({
    volunteers: false,
    projects: false,
  });

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/participations/my-projects`,
        { headers: getAuthHeader() }
      );

      let validProjects = [];
      if (response.data?.success) {
        validProjects = response.data.data || [];
      } else {
        validProjects = (response.data || [])
          .filter((p) => p && p.project)
          .map((p) => p.project);
      }

      setProjects(validProjects);
      if (validProjects.length > 0) {
        await fetchParticipationData(validProjects);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      setError("Failed to fetch projects. Please try again later.");
      setLoading(false);
    }
  };

  const fetchParticipationData = async (projectsData) => {
    try {
      const promises = projectsData.map((project) =>
        axios.get(
          `${API_BASE_URL}/participations/${project._id}`,
          { headers: getAuthHeader() }
        )
      );
      const responses = await Promise.all(promises);
      const data = responses.reduce((acc, res, idx) => {
        if (res.data) acc[projectsData[idx]._id] = res.data;
        return acc;
      }, {});
      setParticipationData(data);
      calculateHours(data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching participation data:", error);
      setError("Failed to fetch participation data. Please try again later.");
      setLoading(false);
    }
  };

  const calculateHours = (data) => {
    let total = 0;
    let pending = 0;
    Object.values(data).forEach((d) => {
      if (d?.logs) {
        d.logs.forEach((log) => {
          if (log?.status === "approved") total += log.hours || 0;
          else if (log?.status === "pending") pending += log.hours || 0;
        });
      }
    });
    setTotalHours(total);
    setPendingHours(pending);
  };

  const fetchLeaderboard = async () => {
    try {
      setLeaderboardLoading(true);
      const res = await axios.get(
        `${API_BASE_URL}/participations/leaderboard`,
        { headers: getAuthHeader() }
      );
      setLeaderboard(res.data || { topVolunteers: [], topProjects: [] });
    } catch (err) {
      console.error("Error fetching leaderboard:", err);
    } finally {
      setLeaderboardLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
    fetchLeaderboard();
  }, []);

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return { text: "Good Morning", Icon: Sun };
    if (h < 18) return { text: "Good Afternoon", Icon: Sunset };
    return { text: "Good Evening", Icon: Moon };
  };

  const { text: greetingText, Icon: GreetingIcon } = getGreeting();

  // ── Chart Data ──────────────────────────────────────────
  const projectHoursData = {
    labels: projects
      .filter((p) => p !== null)
      .map((p) => p.name || "Unnamed"),
    datasets: [
      {
        label: "Approved Hours",
        data: projects
          .filter((p) => p !== null)
          .map(
            (p) =>
              participationData[p._id]?.logs?.reduce(
                (sum, log) => sum + (log?.status === "approved" ? log?.hours || 0 : 0),
                0
              ) || 0
          ),
        backgroundColor: "#2A9D8F",
        borderRadius: 5,
      },
      {
        label: "Pending Hours",
        data: projects
          .filter((p) => p !== null)
          .map(
            (p) =>
              participationData[p._id]?.logs?.reduce(
                (sum, log) => sum + (log?.status === "pending" ? log?.hours || 0 : 0),
                0
              ) || 0
          ),
        backgroundColor: "#1B3A87",
        borderRadius: 5,
      },
    ],
  };

  const projectHoursOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: { stacked: true, grid: { display: false }, ticks: { font: { family: "Inter", size: 11 } } },
      y: { stacked: true, beginAtZero: true, grid: { color: "rgba(0,0,0,0.04)" }, ticks: { font: { family: "Inter", size: 11 } } },
    },
    plugins: {
      legend: { position: "bottom", labels: { boxWidth: 12, font: { family: "Inter", size: 11 } } },
    },
  };

  const getCategoryDistribution = (projs) => {
    const counts = projs.reduce((acc, p) => {
      if (Array.isArray(p?.categories)) {
        p.categories.forEach((c) => { acc[c] = (acc[c] || 0) + 1; });
      }
      return acc;
    }, {});
    return {
      labels: Object.keys(counts),
      datasets: [{
        data: Object.values(counts),
        backgroundColor: COLORS.slice(0, Object.keys(counts).length),
        borderWidth: 0,
      }],
    };
  };

  const categoryData = getCategoryDistribution(projects);

  const categoryChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: { padding: 14, boxWidth: 11, font: { family: "Inter", size: 10.5 } },
      },
    },
  };

  // ── Style tokens ────────────────────────────────────────
  const cardBase = {
    backgroundColor: "var(--color-surface)",
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-lg)",
    boxShadow: "var(--shadow-sm)",
  };

  // ── Loading / Error states ──────────────────────────────
  if (loading) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}>
        <div className="text-center">
          <Spinner
            animation="border"
            style={{ color: "var(--color-primary)", width: "2.5rem", height: "2.5rem" }}
          />
          <p style={{ marginTop: "1rem", color: "var(--color-text-muted)", fontSize: "0.9rem" }}>
            Loading your dashboard…
          </p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert
          className="d-flex align-items-center gap-2"
          style={{
            background: "rgba(193,57,43,0.08)",
            border: "1px solid rgba(193,57,43,0.25)",
            color: "var(--color-red)",
            borderRadius: "var(--radius-md)",
          }}>
          <ShieldAlert size={18} />
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <div
      className="d-flex flex-column flex-lg-row"
      style={{ maxWidth: "100vw", overflowX: "hidden", backgroundColor: "var(--color-bg)" }}>
      <Sidebar />

      <Container
        fluid
        className="py-3 px-4"
        style={{ minHeight: "100vh", maxHeight: "100vh", overflowY: "scroll" }}>
        <TopNavBar />

        {/* ── Welcome Banner ───────────────────────────── */}
        <Card
          className="mb-4 mt-3"
          style={{
            backgroundColor: "var(--color-primary)",
            borderRadius: "var(--radius-lg)",
            border: "none",
            boxShadow: "var(--shadow-md)",
          }}>
          <Card.Body className="p-4">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
              <div className="text-start">
                <div className="d-flex align-items-center gap-2 mb-1">
                  <GreetingIcon size={20} color="rgba(255,255,255,0.7)" />
                  <span style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.88rem", fontWeight: 500 }}>
                    {greetingText}
                  </span>
                </div>
                <h2 className="fw-bold mb-1" style={{ color: "#fff", letterSpacing: "-0.5px" }}>
                  {user?.name || "Student"}
                </h2>
                <p className="mb-0" style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.9rem" }}>
                  Welcome to your volunteer workspace dashboard.
                </p>
              </div>
              <div className="d-none d-md-flex align-items-center gap-3">
                <NotificationIcon variant="white" />
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* ── Metric Cards ─────────────────────────────── */}
        <Row className="mb-4 g-3">
          {[
            {
              label: "Total Projects",
              value: projects.length,
              unit: "",
              Icon: Folder,
              iconBg: "rgba(27,58,135,0.08)",
              iconColor: "var(--color-primary)",
            },
            {
              label: "Approved Hours",
              value: totalHours.toFixed(1),
              unit: "hrs",
              Icon: Clock,
              iconBg: "rgba(42,157,143,0.10)",
              iconColor: "var(--color-teal)",
            },
            {
              label: "Pending Hours",
              value: pendingHours.toFixed(1),
              unit: "hrs",
              Icon: Hourglass,
              iconBg: "rgba(181,131,42,0.10)",
              iconColor: "var(--color-gold)",
            },
          ].map(({ label, value, unit, Icon, iconBg, iconColor }) => (
            <Col xs={6} md={4} key={label}>
              <Card style={cardBase} className="p-3 h-100">
                <div className="d-flex align-items-center justify-content-between text-start">
                  <div>
                    <div
                      style={{
                        color: "var(--color-text-muted)",
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        marginBottom: "0.4rem",
                      }}>
                      {label}
                    </div>
                    <div style={{ fontSize: "1.85rem", fontWeight: 800, color: "var(--color-text)", lineHeight: 1 }}>
                      {value}
                      {unit && (
                        <span style={{ fontSize: "0.85rem", fontWeight: 500, color: "var(--color-text-muted)", marginLeft: "4px" }}>
                          {unit}
                        </span>
                      )}
                    </div>
                  </div>
                  <div
                    style={{
                      padding: "0.7rem",
                      borderRadius: "var(--radius-md)",
                      backgroundColor: iconBg,
                      color: iconColor,
                      flexShrink: 0,
                    }}>
                    <Icon size={20} />
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* ── Charts ───────────────────────────────────── */}
        <Row className="mb-4 g-3">
          <Col lg={8}>
            <Card style={cardBase} className="p-4 h-100 text-start">
              <h6
                style={{
                  color: "var(--color-primary)",
                  fontWeight: 700,
                  letterSpacing: "-0.2px",
                  marginBottom: "1rem",
                }}>
                Project Hours Breakdown
              </h6>
              <div style={{ height: "260px", width: "100%" }}>
                <Bar options={projectHoursOptions} data={projectHoursData} />
              </div>
            </Card>
          </Col>
          <Col lg={4}>
            <Card style={cardBase} className="p-4 h-100 text-start">
              <h6
                style={{
                  color: "var(--color-primary)",
                  fontWeight: 700,
                  letterSpacing: "-0.2px",
                  marginBottom: "1rem",
                }}>
                Project Categories
              </h6>
              <div style={{ height: "260px", width: "100%" }}>
                {projects.length > 0 ? (
                  <Pie data={categoryData} options={categoryChartOptions} />
                ) : (
                  <div className="d-flex align-items-center justify-content-center h-100"
                    style={{ color: "var(--color-text-muted)", fontSize: "0.9rem" }}>
                    No projects available.
                  </div>
                )}
              </div>
            </Card>
          </Col>
        </Row>

        {/* ── Leaderboards ─────────────────────────────── */}
        <Row className="mb-4 g-3">
          {[
            {
              title: "Volunteer Leaderboard",
              Icon: Trophy,
              iconColor: "var(--color-gold)",
              data: leaderboard.topVolunteers,
              colLabel: "Volunteer",
              boardKey: "volunteers",
              highlightCurrentUser: true,
            },
            {
              title: "Project Competition Board",
              Icon: Flame,
              iconColor: "var(--color-red)",
              data: leaderboard.topProjects,
              colLabel: "Project",
              boardKey: "projects",
              highlightCurrentUser: false,
            },
          ].map(({ title, Icon, iconColor, data, colLabel, boardKey, highlightCurrentUser }) => (
            <Col lg={6} key={title}>
              <Card style={cardBase} className="p-4 h-100 text-start">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <Icon size={18} color={iconColor} />
                  <h6 style={{ color: "var(--color-primary)", fontWeight: 700, margin: 0, letterSpacing: "-0.2px" }}>
                    {title}
                  </h6>
                </div>
                <LeaderboardTable
                  data={data}
                  loading={leaderboardLoading}
                  colLabel={colLabel}
                  showAll={expandedLeaderboards[boardKey]}
                  onToggle={() =>
                    setExpandedLeaderboards((current) => ({
                      ...current,
                      [boardKey]: !current[boardKey],
                    }))
                  }
                  highlightCurrentUser={highlightCurrentUser}
                />
              </Card>
            </Col>
          ))}
        </Row>

        {/* ── Active Projects Table ─────────────────────── */}
        <Row className="mb-4">
          <Col xs={12}>
            <Card style={cardBase} className="p-4 text-start">
              <h6 style={{ color: "var(--color-primary)", fontWeight: 700, letterSpacing: "-0.2px", marginBottom: "1rem" }}>
                My Active Projects
              </h6>
              {projects.length > 0 ? (
                <div className="table-responsive">
                  <table className="wcco-table">
                    <thead>
                      <tr>
                        <th>Project Name</th>
                        <th>Categories</th>
                        <th className="text-center">Hours Logged</th>
                        <th className="text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projects
                        .filter((p) => p !== null)
                        .map((project) => {
                          const hrs = (
                            participationData[project._id]?.logs?.reduce(
                              (sum, log) => sum + (log?.hours || 0),
                              0
                            ) || 0
                          ).toFixed(1);

                          return (
                            <tr key={project._id}>
                              <td style={{ fontWeight: 600, color: "var(--color-text)" }}>
                                {project.name || "Unnamed Project"}
                              </td>
                              <td>
                                <div className="d-flex flex-wrap gap-1">
                                  {Array.isArray(project.categories) && project.categories.length > 0 ? (
                                    project.categories.map((cat, i) => (
                                      <span key={i} className="badge-teal">{cat}</span>
                                    ))
                                  ) : (
                                    <span className="badge-primary">Uncategorized</span>
                                  )}
                                </div>
                              </td>
                              <td className="text-center">
                                <span style={{ fontWeight: 700, color: "var(--color-text)" }}>
                                  {hrs}
                                  <span style={{ fontWeight: 400, color: "var(--color-text-muted)", marginLeft: "3px", fontSize: "0.8rem" }}>hrs</span>
                                </span>
                              </td>
                              <td className="text-center">
                                <span
                                  style={{
                                    display: "inline-block",
                                    padding: "0.2em 0.8em",
                                    borderRadius: "99px",
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    background: project.approved
                                      ? "rgba(42,157,143,0.12)"
                                      : "rgba(181,131,42,0.12)",
                                    color: project.approved
                                      ? "var(--color-teal)"
                                      : "var(--color-gold)",
                                  }}>
                                  {project.approved ? "Active" : "Pending Approval"}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div
                  className="text-center py-5"
                  style={{ color: "var(--color-text-muted)", fontSize: "0.9rem" }}>
                  You are not registered in any volunteer projects yet.
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default StudentDashboard;
